import { useMemo, useState } from "react";
import { Calendar as CalIcon } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

// Parse any value shape used in the app: ISO yyyy-mm-dd, dd/mm/yyyy, or Date.
function parseAny(v) {
  if (!v) return null;
  if (v instanceof Date && !isNaN(v)) return v;
  const s = String(v).trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  return null;
}
const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => (d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : "");
const toDMY = (d) => (d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : "");
function mondayOf(d) { const x = new Date(d); const g = (x.getDay() + 6) % 7; x.setDate(x.getDate() - g); x.setHours(0,0,0,0); return x; }

/**
 * Shared NZ-format date picker.
 * Props mirror a native <input type="date">: value + onChange(e).
 *   - `value` may be ISO yyyy-mm-dd OR dd/mm/yyyy; auto-detected.
 *   - `onChange(e)` is called with `e.target.value` in the requested `format`
 *     ("iso" default; "dmy" for screens that store dd/mm/yyyy strings).
 *   - `showQuickPicks` adds This/Last week + month buttons.
 *   - `onQuickPick({from, to})` is called when a range quick-pick is used;
 *     the parent sets both From and To.
 */
export default function DateInput({
  value, onChange, format = "iso",
  showQuickPicks = false, onQuickPick,
  placeholder = "DD/MM/YYYY", className = "",
  "data-testid": tid, disabled = false, ...rest
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const parsed = useMemo(() => parseAny(value), [value]);
  const display = typed !== "" ? typed : (parsed ? toDMY(parsed) : "");

  function emit(d) {
    const val = d ? (format === "iso" ? toISO(d) : toDMY(d)) : "";
    onChange?.({ target: { value: val } });
    setTyped("");
  }
  function onTyped(e) {
    const v = e.target.value;
    setTyped(v);
    const d = parseAny(v);
    if (d) onChange?.({ target: { value: format === "iso" ? toISO(d) : toDMY(d) } });
    else if (v === "") onChange?.({ target: { value: "" } });
  }
  function pick(d) { emit(d); setOpen(false); }
  function today() { pick(new Date()); }
  function clear() { emit(null); setOpen(false); }
  function qp(kind) {
    const t = new Date(); t.setHours(0,0,0,0);
    let from, to;
    if (kind === "this_week") { from = mondayOf(t); to = new Date(from); to.setDate(from.getDate() + 6); }
    if (kind === "last_week") { from = mondayOf(t); from.setDate(from.getDate() - 7); to = new Date(from); to.setDate(from.getDate() + 6); }
    if (kind === "this_month") { from = new Date(t.getFullYear(), t.getMonth(), 1); to = new Date(t.getFullYear(), t.getMonth() + 1, 0); }
    if (kind === "last_month") { from = new Date(t.getFullYear(), t.getMonth() - 1, 1); to = new Date(t.getFullYear(), t.getMonth(), 0); }
    if (format === "iso") onQuickPick?.({ from: toISO(from), to: toISO(to) });
    else onQuickPick?.({ from: toDMY(from), to: toDMY(to) });
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative inline-block w-full">
        <input
          type="text" inputMode="numeric" autoComplete="off"
          className={cn("input pr-9", className)}
          value={display} onChange={onTyped} placeholder={placeholder}
          disabled={disabled} data-testid={tid}
          onKeyDown={(e) => { if (e.key === "Enter") { setOpen(true); e.preventDefault(); } }}
          {...rest}
        />
        <PopoverTrigger asChild>
          <button
            type="button" disabled={disabled}
            onClick={() => setOpen((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-silver hover:text-white disabled:opacity-30"
            aria-label="Open calendar"
            data-testid={tid ? `${tid}-btn` : "di-btn"}
          >
            <CalIcon className="h-4 w-4" />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent align="start" className="w-auto p-2 bg-black border border-neutral-700 text-white">
        <Calendar
          mode="single" selected={parsed || undefined} onSelect={pick}
          initialFocus weekStartsOn={1}
          className="bg-black text-white"
        />
        <div className="flex gap-2 justify-between pt-2 border-t border-neutral-700 mt-1">
          <button type="button" className="btn-silver text-xs px-2 py-1" onClick={today} data-testid="di-today">Today</button>
          <button type="button" className="text-xs text-silver underline" onClick={clear} data-testid="di-clear">Clear</button>
        </div>
        {showQuickPicks && (
          <div className="grid grid-cols-2 gap-1 pt-2" data-testid="di-quick-picks">
            <button type="button" className="text-xs text-silver underline text-left" onClick={() => qp("this_week")} data-testid="di-qp-this-week">This week</button>
            <button type="button" className="text-xs text-silver underline text-left" onClick={() => qp("last_week")} data-testid="di-qp-last-week">Last week</button>
            <button type="button" className="text-xs text-silver underline text-left" onClick={() => qp("this_month")} data-testid="di-qp-this-month">This month</button>
            <button type="button" className="text-xs text-silver underline text-left" onClick={() => qp("last_month")} data-testid="di-qp-last-month">Last month</button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
